import React from 'react';
import * as d3 from 'd3';
import { useI18n } from '@whizz/react-i18n';
import type { HighlightedProps, SchoolReportType } from '../../types';
import { SectionLayout } from '../modules';
import { StudentProgress } from '../../../components';
import type { ChartDataType, ChartIconType } from '../../../components/charts/types';
import { FIXED_AXIS } from '../../../components/charts/helpers/vars';

type Props = {
  schoolReport: SchoolReportType;
  showOthersData: boolean
  iconToUse: ChartIconType
} & HighlightedProps;

const SchoolQualityLearningTime = ({ schoolReport, showOthersData, highlightedLabel, highlightedIds, iconToUse, onElementClick }: Props): JSX.Element => {
  const { translate } = useI18n();

  const getHighlightedAvg = (): string | null => {
    if (highlightedIds && highlightedIds.length > 0) {
      const mean = d3.mean(dataToUse().map((cl) => {
        if (highlightedIds.includes(cl.id)) {
          return cl.chartsData?.qualityLearningTime;
        }
      }));
      return mean != undefined ? roundToDecimal(mean) : null;
    }

    return null;
  };

  const roundToDecimal = (value: number): string => {
    const decimal = Number.isSafeInteger(value) ? '.0f' : '.2f';
    return d3.format(decimal)(value);
  };

  const dataToUse = (): ChartDataType[] => {
    if (iconToUse === 'class') {
      return schoolReport.classes;
    } else return schoolReport.yearGroups;
  };

  const value = roundToDecimal(schoolReport.average.qualityLearningTimeAverage);
  const highlightValue = getHighlightedAvg();

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.QUALITY.title')}
      value={showOthersData ? value : null}
      highlightLabel={highlightedLabel}
      highlightValue={highlightValue}
      unit={translate('pages.REPORTS.SECTIONS.QUALITY.unit', { count: parseFloat(value) })}
      description={translate('pages.REPORTS.SECTIONS.QUALITY.description')}
      moreInfo={translate('pages.REPORTS.SECTIONS.QUALITY.info')}
      id={'school-quality-learning-time-container'}
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={dataToUse()}
        chartType={'qualityLearningTime'}
        type={iconToUse}
        highlightedIds={highlightedIds}
        customAxis={FIXED_AXIS.recent?.qualityLearningTime}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

SchoolQualityLearningTime.displayName = 'SchoolQualityLearningTime';

export default SchoolQualityLearningTime;
